import React, { useEffect } from 'react';
import useBootstrapAnalysisSchedules from './useBootstrapAnalysisSchedules';
import { useScheduleModalHandlers } from '../utils';
import {
  IScheduleFieldsFragment,
  PermissionKey,
  ScheduleType,
} from '../../../utils/apollo/graphqlSchema';
import * as FormUI from '../../pageForm/FormUI';
import TriggerScheduleModal from '../components/TriggerScheduleModal';
import DeleteScheduleModal from '../components/DeleteScheduleModal';
import Scheduler from '../../../components/Scheduler';
import { ScheduleScreenshotModal } from '../components/ScheduleHistory';
import { useAppSelector } from '../../../redux/hooks';
import { isNotNil } from '../../../utils/types';
import { defaultDictionaryStates } from '../../Dictionary/utils/utils';
import { IDictionaryStates } from '../../Dictionary/utils/types';
import { useImmer } from 'use-immer';
import DictionaryHeader from '../../Dictionary/utils/DictionaryHeader';
import usePermissionSchemas from '../../PermissionControls/hooks/usePermissionSchemas';
import { useDebounce } from '../../../utils/hooks';
import { ContentLoader } from '../../../components/Loaders';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { pageViewed } from '../../../utils/analytics/events/page/events';
import ScheduleAnalysisTable from './ScheduleAnalysisTable';

const scheduleType = ScheduleType.Analysis;

export default () => {
  const { track } = useAnalytics();
  useEffect(() => {
    track(
      pageViewed({
        category: 'schedule',
        name: 'report',
        id: null,
      }),
    );
  }, [track]);
  const { selectedSchemaId, permittedSchemaIds } = usePermissionSchemas({
    requestedPermissionKey: PermissionKey.AnalysisScheduleRead,
  });

  const [dictionaryStates, setDictionaryStates] = useImmer<IDictionaryStates>(
    defaultDictionaryStates,
  );
  const debouncedSearchQuery = useDebounce(dictionaryStates.searchTerm, 300);
  const loading = useBootstrapAnalysisSchedules({
    schemaId: selectedSchemaId,
    searchTerm: debouncedSearchQuery,
  });
  const screenshotSelectedImage = useAppSelector(
    (state) => state.scheduler.screenshotSelectedImage,
  );
  const {
    isDeleteScheduleModalOpen,
    isExecuteScheduleModalOpen,
    isEditScheduleModalOpen,
  } = useScheduleModalHandlers();

  const schedules: IScheduleFieldsFragment[] = useAppSelector(
    (state) => state.dictionary.scheduleAnalysis,
  );

  return (
    <>
      <DictionaryHeader
        selectedSchemaId={selectedSchemaId}
        permittedSchemaIds={permittedSchemaIds}
        searchQuery={dictionaryStates.searchTerm}
        setSearchQuery={(newSearchTerm) =>
          setDictionaryStates((draft) => {
            draft.searchTerm = newSearchTerm;
          })
        }
        title={'Report'}
        dictionaryItemCount={schedules?.length ?? 0}
        isPermittedToAssignCategory={false}
      />
      <FormUI.Container>
        {schedules?.length > 0 ? (
          <>
            <ScheduleAnalysisTable schedules={schedules} />
            {isExecuteScheduleModalOpen && (
              <TriggerScheduleModal type={scheduleType} />
            )}
            {isDeleteScheduleModalOpen && (
              <DeleteScheduleModal type={scheduleType} />
            )}
            {isEditScheduleModalOpen && (
              <Scheduler jobType={ScheduleType.Analysis} />
            )}
          </>
        ) : (
          loading && <ContentLoader noBorders={true} />
        )}
      </FormUI.Container>
      {isNotNil(screenshotSelectedImage) && <ScheduleScreenshotModal />}
    </>
  );
};
